import { Keyboard } from '@frontend/icons';
import { LuDelete } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { PinCodeLockoutModal, PinCodeSubmitHint } from '../../components';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { DEFAULT_PIN_LENGTH } from '../../utils/pinCodeCache';
import useDropOffPinCode from './drop-off-pin-code.controller';

const ID = 'drop-off-pin-code';

const DropOffPinCode = () => {
    const viewProps = useDropOffPinCode();

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: !viewProps.canSubmit
            }}
            loadingModal={{
                show: viewProps.resolving,
                onClose: viewProps.stopResolving
            }}
            containerClassName='bg-background-default flex flex-1 flex-col items-center justify-start gap-8 self-stretch overflow-y-scroll p-6 pb-20'>
            {/* Header with keyboard icon */}
            <div className='inline-flex items-center justify-center gap-3 self-stretch'>
                <div className='relative h-6 w-6 overflow-hidden'>
                    <Keyboard className='text-always-black size-6' />
                </div>
                <div className='text-always-black justify-end text-lg font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.drop-off-method.input-pin'
                        defaultMessage='Input PIN-code'
                        description='Label for button to input PIN-code manually'
                    />
                </div>
            </div>

            {/* PIN-code input display */}
            <div className='flex justify-center gap-3'>
                {viewProps.isLoadingPinLength
                    ? // Loading skeleton
                      Array.from({ length: DEFAULT_PIN_LENGTH }).map((_, index) => (
                          <div
                              key={index}
                              className='flex h-9 w-9 animate-pulse items-center justify-center border-b-2 border-gray-300 bg-gray-100'></div>
                      ))
                    : Array.from({ length: viewProps.pinLength }).map((_, index) => (
                          <div
                              key={index}
                              className={`flex h-9 w-9 items-center justify-center border-b-2 ${
                                  viewProps.hasError ? 'border-status-danger' : 'border-brand-muted'
                              }`}>
                              {viewProps.pinCode[index] && (
                                  <p className={`text-2xl font-medium ${viewProps.hasError ? 'text-status-danger' : 'text-always-black'}`}>
                                      {viewProps.pinCode[index]}
                                  </p>
                              )}
                          </div>
                      ))}
            </div>

            {/* Number pad */}
            <div className='flex w-full flex-col items-center gap-2 px-0 py-4'>
                {/* Row 1: 1, 2, 3 */}
                <div className='flex w-full items-center justify-center gap-2'>
                    {[1, 2, 3].map((num) => (
                        <button
                            key={num}
                            onClick={() => viewProps.onNumberPress(num)}
                            disabled={viewProps.pinCode.length >= viewProps.pinLength || viewProps.isLoadingPinLength}
                            className='bg-background-surface border-border-default flex size-20 items-center justify-center rounded-full border transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'>
                            <p className='text-always-black text-2xl font-medium'>{num}</p>
                        </button>
                    ))}
                </div>

                {/* Row 2: 4, 5, 6 */}
                <div className='flex w-full items-center justify-center gap-2'>
                    {[4, 5, 6].map((num) => (
                        <button
                            key={num}
                            onClick={() => viewProps.onNumberPress(num)}
                            disabled={viewProps.pinCode.length >= viewProps.pinLength || viewProps.isLoadingPinLength}
                            className='bg-background-surface border-border-default flex size-20 items-center justify-center rounded-full border transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'>
                            <p className='text-always-black text-2xl font-medium'>{num}</p>
                        </button>
                    ))}
                </div>

                {/* Row 3: 7, 8, 9 */}
                <div className='flex w-full items-center justify-center gap-2'>
                    {[7, 8, 9].map((num) => (
                        <button
                            key={num}
                            onClick={() => viewProps.onNumberPress(num)}
                            disabled={viewProps.pinCode.length >= viewProps.pinLength || viewProps.isLoadingPinLength}
                            className='bg-background-surface border-border-default flex size-20 items-center justify-center rounded-full border transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'>
                            <p className='text-always-black text-2xl font-medium'>{num}</p>
                        </button>
                    ))}
                </div>

                {/* Row 4: Backspace, 0, Empty */}
                <div className='flex w-full items-center justify-center gap-2'>
                    <div className='size-20' /> {/* Empty space for alignment */}
                    <button
                        onClick={() => viewProps.onNumberPress(0)}
                        disabled={viewProps.pinCode.length >= viewProps.pinLength || viewProps.isLoadingPinLength}
                        className='bg-background-surface border-border-default flex size-20 items-center justify-center rounded-full border transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'>
                        <p className='text-always-black text-2xl font-medium'>0</p>
                    </button>
                    <button
                        onClick={viewProps.onBackspace}
                        className='flex size-20 items-center justify-center rounded-full transition-colors hover:bg-gray-50 active:bg-gray-100'>
                        <LuDelete className='text-always-black size-6' />
                    </button>
                </div>
            </div>

            <PinCodeSubmitHint show={viewProps.canSubmit && !viewProps.hasError && !viewProps.resolving} />

            {/* Error message */}
            {viewProps.hasError && (
                <div className='text-status-danger mt-8 text-center font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.pin-code.error'
                        defaultMessage='Wrong PIN-code. Please try again.'
                        description='Error message when wrong PIN-code is entered'
                    />
                </div>
            )}

            {/* Lockout Modal */}
            <PinCodeLockoutModal
                show={viewProps.showModal}
                remainingTime={viewProps.remainingTime}
                onClose={viewProps.closeModal}
            />
        </WizardScreenContainer>
    );
};

export default DropOffPinCode;
